
import { defineComponent, ref, computed, reactive, watch, nextTick, PropType, onMounted, onUnmounted } from 'vue'
import starApi from '@/api/starApi'
import {
  normalizeSelectedOptionsFromTree,
  resolveFactorSubLabelText,
  stripWatchwordSelectionDescript,
} from '@/utils/starFactorLabel'
import FiLabelSub from './FiLabelSub.vue'
import { formatFactorRangeSubLabel, mergeRangeOps } from '@/utils/starRangeBounds'
import type { SelectedRangeState } from '@/utils/starRangeBounds'

/** 本文件由 FactorItem.vue 复制，仅用于「第二步：选股范围」。FactorItem.vue 请勿改此副本的逻辑基线；选股范围专属改动只在本文件维护。 */

const ASYNC_TYPES = new Set([5, 6, 7])
const MULTI_TYPES = new Set([3, 4, 5, 6])

export default defineComponent({
  name: 'StockRangeFactorItem',
  components: { FiLabelSub },
  props: {
    factor: { type: Object as PropType<any>, required: true },
    selected: Boolean,
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    headers: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
    highlight: Boolean,
    subLabel: { type: String, default: '' },
    showSubLabel: { type: Boolean, default: false }
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const showPopup = ref(false)
    const popupPos = reactive({ x: 0, y: 0 })
    const loadingOptions = ref(false)
    const asyncOptions = ref<any[]>([])
    /** 区间因子：接口每只股票一行，用于分布、计数及确认时展开 selectedOptions（选股范围专用） */
    const rangeStockRows = ref<{ value: number; attrCode: string; attrName: string }[]>([])
    const pendingOptions = ref<any[]>([])
    const unionMode = ref<'union' | 'intersect'>('union')
    const searchKw = ref('')
    const showDescTip = ref(false)
    const descTipPos = reactive({ x: 0, y: 0 })

    const onDescBtnEnter = (e: MouseEvent) => {
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
      descTipPos.x = r.left
      descTipPos.y = r.bottom + 6
      showDescTip.value = true
    }

    const onDescBtnLeave = () => {
      showDescTip.value = false
    }

    // 区间类型
    const rangeLeft = ref('')
    const rangeRight = ref('')
    const rangeError = ref('')
    const rangeStockCount = ref(0)
    const rangeStockPct = ref('0.00')
    // 内部记忆已确认的区间，不依赖 props.factor.selectedRange
    const savedRange = ref<SelectedRangeState | null>(null)
    const chartCanvas = ref<HTMLCanvasElement | null>(null)
    const defaultLeft = ref('')
    const defaultRight = ref('')

    const isMulti = computed(() => MULTI_TYPES.has(props.factor.attrType))
    const hasUnionSwitch = computed(() => [4, 6].includes(props.factor.attrType))

    const optionList = computed(() => {
      if (ASYNC_TYPES.has(props.factor.attrType)) return asyncOptions.value
      return props.factor.childAttrList || []
    })

    const filteredOptions = computed(() => {
      if (!searchKw.value.trim()) return optionList.value
      const kw = searchKw.value.trim().toLowerCase()
      return optionList.value.filter((o: any) => o.attrName?.toLowerCase().includes(kw))
    })

    const pendingCodes = computed(() => new Set(pendingOptions.value.map((o: any) => o.attrCode)))

    const optionListWithDesc = computed(() => optionList.value.filter((o: any) => o.attrDesc))
    const hasOptionDescs = computed(() => optionListWithDesc.value.length > 0)

    const isRangeChanged = computed(() =>
      rangeLeft.value !== defaultLeft.value || rangeRight.value !== defaultRight.value
    )

    const canConfirm = computed(() => {
      const t = props.factor.attrType
      if (t === 7) {
        const hasLeft = String(rangeLeft.value).trim() !== ''
        const hasRight = String(rangeRight.value).trim() !== ''
        return !rangeError.value && (hasLeft || hasRight)
      }
      if ([1, 2, 3, 4, 5, 6].includes(t)) {
        return pendingOptions.value.length > 0
      }
      return true
    })

    const confirmDisabled = computed(() => loadingOptions.value || !canConfirm.value)

    const confirmBtnStyle = computed(() => ({
      background: confirmDisabled.value ? '#ccc' : '#e8392a',
      color: '#fff',
      cursor: confirmDisabled.value ? 'not-allowed' : 'pointer',
    }))

    const rangeSortedValues = computed(() =>
      rangeStockRows.value.map(row => row.value).sort((a, b) => a - b)
    )

    const mapApiRowToRangeStock = (d: any) => {
      const value = Number(d.value ?? 0)
      const attrCode = String(d.symbol ?? d.securityNo ?? d.optionCode ?? d.attrCode ?? '').trim()
      const attrName = String(
        d.stockName ?? d.securityName ?? d.name ?? d.optionName ?? d.attrName ?? attrCode
      ).trim()
      return { value, attrCode, attrName: attrName || attrCode }
    }

    // 计算默认区间值，完全对齐客户端 CFactorRangeAlgorithm::CalcRange 逻辑
    // 客户端返回 valueVec[nLeft..nRight)，左右边界取子数组首尾元素
    const calcDefaultRange = (factor: any) => {
      const sorted: number[] = factor.distribution || []
      if (!sorted.length) return { left: '', right: '' }

      const nSize = sorted.length
      const round2 = (v: number) => Math.round(v * 100) / 100
      // defaultRangeType 文档编号从0开始，C++ 枚举从1开始，需要+1对齐
      const type = (factor.defaultRangeType != null ? factor.defaultRangeType + 1 : 1)

      // C++ size_t 乘法截断 = Math.trunc（等同 Math.floor，因为结果为正）
      const trunc = (v: number) => Math.trunc(v)

      let nLeft = 0, nRight = 0

      switch (type) {
        case 1: // 中位数前后各取15%（第35~65分位）
          nLeft  = trunc(nSize * 0.35)
          nRight = trunc(nSize * 0.65)
          break
        case 2: // 中位数前后各取25%（第25~75分位）
          nLeft  = trunc(nSize * 0.25)
          nRight = trunc(nSize * 0.75)
          break
        case 3: { // ≤0 的数据，取中位数前100%
          let idx = -1
          for (let i = nSize - 1; i >= 0; i--) { if (sorted[i] <= 0) { idx = i; break } }
          if (idx < 0) return { left: '', right: '' }
          nLeft = 0
          nRight = nSize - (nSize - 1 - idx)  // = idx + 1
          break
        }
        case 4: // 取中位数后100%
          nLeft  = trunc(nSize * 0.5)
          nRight = trunc(nSize * 1)  // = nSize
          break
        case 5: { // ≥0 的数据全部
          const idx = sorted.findIndex((v: number) => v >= 0)
          if (idx < 0) return { left: '', right: '' }
          nLeft = idx
          nRight = nSize
          break
        }
        case 6: { // ≥0 数据里，取中位数后100%
          const nBegin = sorted.findIndex((v: number) => v >= 0)
          if (nBegin < 0) return { left: '', right: '' }
          const nMid = nBegin + Math.trunc((nSize - 1 - nBegin) / 2)
          nLeft = nMid
          nRight = nSize
          break
        }
        case 7: { // ≥0 数据里，取中位数前100%
          const nBegin = sorted.findIndex((v: number) => v >= 0)
          if (nBegin < 0) return { left: '', right: '' }
          const nMid = nBegin + Math.trunc((nSize - 1 - nBegin) / 2)
          nLeft = 0
          nRight = Math.trunc(nMid / 2)
          break
        }
        default:
          return { left: '', right: '' }
      }

      // 对齐 C++：nLeft <= nRight 才有效（注意客户端是 <=，不是 <）
      if (nLeft > nRight || nRight > nSize) return { left: '', right: '' }
      // 子数组 [nLeft, nRight)，取首尾
      return {
        left:  String(round2(sorted[nLeft])),
        right: String(round2(sorted[nRight - 1]))
      }
    }

    const loadAsyncOptions = async () => {
      if (!ASYNC_TYPES.has(props.factor.attrType)) return
      loadingOptions.value = true
      try {
        const res = await starApi.getStockFactorOptions({
          data: { factorCode: props.factor.attrCode },
          headers: props.headers
        })
        const raw: any[] = res?.data || []

        if (props.factor.attrType === 7) {
          rangeStockRows.value = raw
            .map(mapApiRowToRangeStock)
            .filter((row: { value: number; attrCode: string }) => isFinite(row.value) && !!row.attrCode)
          const def = calcDefaultRange({ ...props.factor, distribution: rangeSortedValues.value })
          defaultLeft.value = def.left
          defaultRight.value = def.right
          const fromProps = props.factor.selectedRange
          const hasPropsRange =
            props.factor._confirmed ||
            (fromProps && (fromProps.left !== '' || fromProps.right !== ''))
          const confirmedRange =
            savedRange.value ??
            (hasPropsRange
              ? mergeRangeOps(
                  { left: String(fromProps?.left ?? ''), right: String(fromProps?.right ?? '') },
                  fromProps,
                )
              : null)
          if (confirmedRange && (confirmedRange.left !== '' || confirmedRange.right !== '' || props.factor._confirmed)) {
            rangeLeft.value = confirmedRange.left ?? ''
            rangeRight.value = confirmedRange.right ?? ''
            if (!savedRange.value && props.factor._confirmed) {
              savedRange.value = { ...confirmedRange }
            }
          } else {
            rangeLeft.value = def.left
            rangeRight.value = def.right
          }
          await nextTick()
          onRangeInput()
          drawChart()
        } else {
          asyncOptions.value = raw.map((o: any) => ({
            ...o,
            attrCode: String(o.plateCode ?? o.optionCode ?? o.attrCode ?? ''),
            attrName: String(o.plateName ?? o.optionName ?? o.attrName ?? ''),
            changeRate: o.pxChangeRate ?? o.changeRate,
          })).filter((o: any) => o.attrCode)
          if (pendingOptions.value.length) {
            pendingOptions.value = normalizeSelectedOptionsFromTree(
              pendingOptions.value,
              asyncOptions.value,
            )
          }
        }
      } catch (e) { /* 静默 */ }
      finally { loadingOptions.value = false }
    }

    // 分布图绘制（直接用原始数据，每个值按位置画竖线，不分桶）
    const drawChart = () => {
      const canvas = chartCanvas.value
      if (!canvas) return
      const sorted = rangeSortedValues.value
      if (!sorted.length) return
      const W = canvas.offsetWidth || 320
      const H = 60
      canvas.width = W
      canvas.height = H
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const minVal = sorted[0]
      const maxVal = sorted[sorted.length - 1]
      const range = maxVal - minVal || 1

      const lv = rangeLeft.value !== '' ? Number(rangeLeft.value) : -Infinity
      const rv = rangeRight.value !== '' ? Number(rangeRight.value) : Infinity

      // 先统计每个像素列有多少个值，用于高度归一化
      const colCounts = new Array(W).fill(0)
      for (const v of sorted) {
        const col = Math.min(Math.floor(((v - minVal) / range) * W), W - 1)
        colCounts[col]++
      }
      let maxCount = 1
      for (let i = 0; i < colCounts.length; i++) {
        if (colCounts[i] > maxCount) maxCount = colCounts[i]
      }

      ctx.clearRect(0, 0, W, H)
      for (let col = 0; col < W; col++) {
        if (colCounts[col] === 0) continue
        const bucketVal = minVal + (col / W) * range
        const inRange = bucketVal >= lv && bucketVal <= rv
        const barH = Math.max(2, (colCounts[col] / maxCount) * (H - 4))
        ctx.fillStyle = inRange
          ? '#e8392a'
          : '#ddd'
        ctx.fillRect(col, H - barH, 1, barH)
      }

      // x 轴底部实线
      ctx.strokeStyle = '#ccc'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(0, H - 1)
      ctx.lineTo(W, H - 1)
      ctx.stroke()
    }

    const onRangeInput = () => {
      const l = rangeLeft.value !== '' ? Number(rangeLeft.value) : null
      const r = rangeRight.value !== '' ? Number(rangeRight.value) : null
      if (l !== null && r !== null && l > r) {
        rangeError.value = '左值必须≤右值'
      } else {
        rangeError.value = ''
      }
      const rows = rangeStockRows.value
      if (rows.length) {
        const total = rows.length
        const inRange = rows.filter(row =>
          (l === null || row.value >= l) && (r === null || row.value <= r)
        ).length
        rangeStockCount.value = inRange
        rangeStockPct.value = total ? ((inRange / total) * 100).toFixed(2) : '0.00'
      } else {
        rangeStockCount.value = 0
        rangeStockPct.value = '0.00'
      }
      drawChart()
    }

    const restoreDefault = () => {
      rangeLeft.value = defaultLeft.value
      rangeRight.value = defaultRight.value
      rangeError.value = ''
      onRangeInput()
    }

    // 拖拽分布图调整区间
    const onChartDrag = (e: MouseEvent) => {
      const canvas = chartCanvas.value
      if (!canvas) return
      const sorted = rangeSortedValues.value
      if (!sorted.length) return
      const rect = canvas.getBoundingClientRect()
      const minVal = sorted[0]
      const maxVal = sorted[sorted.length - 1]
      const range = maxVal - minVal || 1
      const pct = (e.clientX - rect.left) / rect.width
      const val = Math.round((minVal + pct * range) * 100) / 100
      const lv = rangeLeft.value !== '' ? Number(rangeLeft.value) : minVal
      const rv = rangeRight.value !== '' ? Number(rangeRight.value) : maxVal
      const midPct = ((lv + rv) / 2 - minVal) / range
      if (pct < midPct) rangeLeft.value = String(val)
      else rangeRight.value = String(val)
      onRangeInput()
    }

    // 坐标轴 min/max 直接取原始数据
    const distMin = computed(() => {
      const s = rangeSortedValues.value
      if (!s.length) return ''
      return String(Math.round(s[0] * 100) / 100)
    })
    const distMax = computed(() => {
      const s = rangeSortedValues.value
      if (!s.length) return ''
      return String(Math.round(s[s.length - 1] * 100) / 100)
    })

    // 滑块手柄位置
    const leftHandlePos = computed(() => {
      const s = rangeSortedValues.value
      if (!s.length) return 0
      const minVal = s[0], maxVal = s[s.length - 1]
      const range = maxVal - minVal || 1
      const lv = rangeLeft.value !== '' ? Number(rangeLeft.value) : minVal
      return Math.max(0, Math.min(100, ((lv - minVal) / range) * 100))
    })

    const rightHandlePos = computed(() => {
      const s = rangeSortedValues.value
      if (!s.length) return 100
      const minVal = s[0], maxVal = s[s.length - 1]
      const range = maxVal - minVal || 1
      const rv = rangeRight.value !== '' ? Number(rangeRight.value) : maxVal
      return Math.max(0, Math.min(100, ((rv - minVal) / range) * 100))
    })

    // 拖拽滑块手柄
    const onHandleDrag = (side: 'left' | 'right', e: MouseEvent) => {
      e.preventDefault()
      const canvas = chartCanvas.value
      if (!canvas) return
      const sorted = rangeSortedValues.value
      if (!sorted.length) return
      const minVal = sorted[0], maxVal = sorted[sorted.length - 1]
      const range = maxVal - minVal || 1

      const onMove = (me: MouseEvent) => {
        const rect = canvas.getBoundingClientRect()
        const pct = Math.max(0, Math.min(1, (me.clientX - rect.left) / rect.width))
        const val = Math.round((minVal + pct * range) * 100) / 100
        if (side === 'left') rangeLeft.value = String(val)
        else rangeRight.value = String(val)
        onRangeInput()
      }
      const onUp = () => {
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

    const factorAttrType = computed(() => Number(props.factor.attrType))

    // 选中内容回显文字（优先用内部计算，没有则用外部传入的 subLabel）
    const displaySubLabel = computed(() => {
      const f = props.factor
      const t = factorAttrType.value
      if (f._confirmed && [3, 4, 5, 6].includes(t)) {
        const text = resolveFactorSubLabelText(f)
        if (text) return text
      }
      if (!props.selected) return props.subLabel
      switch (t) {
        case 2:
        case 3:
        case 4:
        case 5:
        case 6:
          return resolveFactorSubLabelText(f)
        case 7: {
          const r = savedRange.value || f.selectedRange
          const cnt = (f.selectedOptions || []).length
          const rangeStr = formatFactorRangeSubLabel(
            f.attrName || '',
            r,
            f.attrUnit || f.unit || '',
          ) || ''
          if (rangeStr && cnt > 0) return `${rangeStr}（${cnt}只）`
          if (rangeStr) return rangeStr
          return ''
        }
        default: return props.subLabel
      }
    })

    const hasSubLabelArea = computed(() => {
      if (!props.showSubLabel) return false
      if (displaySubLabel.value) return true
      const t = factorAttrType.value
      return props.selected && [3, 4, 5, 6, 7].includes(t)
    })

    /** 卡片 ×：通知父级从已选中移除（区间/多选等不能再用「再 emit 一遍 factor」当删除） */
    const onRemoveTag = (e: MouseEvent) => {
      e.stopPropagation()
      emit('toggle', { ...props.factor, _tagRemove: true })
    }

    const onClick = (e: MouseEvent) => {
      e.stopPropagation()
      // 类型1：固定选项，直接 toggle 不弹框
      if (props.factor.attrType === 1 || (props.factor.attrType == null && !props.factor.childAttrList?.length)) {
        emit('toggle', props.factor)
        return
      }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const popupW = [5, 6, 7].includes(props.factor.attrType) ? 320 : 280
      // 弹框预估高度：type7(区间)最高，其他按选项数估算，最大取400
      const estimatedH = props.factor.attrType === 7 ? 320
        : [5, 6].includes(props.factor.attrType) ? 360
        : Math.min(400, 80 + ((props.factor.childAttrList?.length || 4) * 40))
      // 底部留出 summary-bar 高度（约130px）+ 弹框间距
      const summaryBarH = 130
      const spaceBelow = window.innerHeight - rect.bottom - summaryBarH
      const spaceAbove = rect.top
      // 左右：优先左对齐，右侧不够则右对齐
      const spaceRight = window.innerWidth - rect.left
      popupPos.x = spaceRight >= popupW + 8
        ? rect.left + window.scrollX
        : Math.max(8, window.innerWidth - popupW - 8) + window.scrollX
      // 上下：下方空间够就向下弹，否则向上弹
      if (spaceBelow >= estimatedH || spaceBelow >= spaceAbove) {
        popupPos.y = rect.bottom + 6 + window.scrollY
      } else {
        popupPos.y = rect.top - estimatedH - 6 + window.scrollY
      }
      // 初始化 pending（编辑模式带入已选）
      unionMode.value = props.factor.unionMode === 'intersect' ? 'intersect' : 'union'
      pendingOptions.value = normalizeSelectedOptionsFromTree(
        props.factor.selectedOptions ? [...props.factor.selectedOptions] : [],
        optionList.value,
      )
      if (props.factor.attrType === 7) {
        rangeLeft.value = savedRange.value?.left ?? props.factor.selectedRange?.left ?? ''
        rangeRight.value = savedRange.value?.right ?? props.factor.selectedRange?.right ?? ''
      }
      showPopup.value = true
      loadAsyncOptions()
    }

    const toggleOption = (opt: any) => {
      if (opt.locked) {
        // 上锁选项：打开营销H5（此处仅占位）
        return
      }
      if ([1, 2].includes(props.factor.attrType)) {
        // 单选
        pendingOptions.value = pendingCodes.value.has(opt.attrCode) ? [] : [opt]
      } else {
        // 多选
        const idx = pendingOptions.value.findIndex((o: any) => o.attrCode === opt.attrCode)
        if (idx >= 0) pendingOptions.value.splice(idx, 1)
        else pendingOptions.value.push(opt)
      }
    }

    const removePending = (opt: any) => {
      pendingOptions.value = pendingOptions.value.filter((o: any) => o.attrCode !== opt.attrCode)
    }

    const onCancel = () => {
      showPopup.value = false
      showDescTip.value = false
      pendingOptions.value = []
      searchKw.value = ''
    }

    const onConfirm = () => {
      if (!canConfirm.value) return
      if (props.factor.attrType === 7) {
        if (rangeError.value) return
        // 内部记忆已选区间
        savedRange.value = mergeRangeOps(
          {
            left: String(rangeLeft.value).trim(),
            right: String(rangeRight.value).trim(),
          },
          savedRange.value ?? props.factor.selectedRange,
        )
        const l = rangeLeft.value !== '' ? Number(rangeLeft.value) : -Infinity
        const r = rangeRight.value !== '' ? Number(rangeRight.value) : Infinity
        const stocksInRange = rangeStockRows.value
          .filter(row => row.value >= l && row.value <= r)
          .map(row => ({ attrCode: row.attrCode, attrName: row.attrName }))
        const { selectionDescript: _d1, ...factorRest } = props.factor
        emit('toggle', {
          ...factorRest,
          selectedRange: savedRange.value,
          selectedOptions: stocksInRange,
          _confirmed: true
        })
      } else if (pendingOptions.value.length > 0) {
        const selectedOptions = normalizeSelectedOptionsFromTree(
          pendingOptions.value,
          optionList.value,
        )
        emit('toggle', {
          ...stripWatchwordSelectionDescript({
            ...props.factor,
            selectedOptions,
            unionMode: unionMode.value,
            _confirmed: true,
          }),
          _userModified: true,
        })
      }
      showPopup.value = false
      searchKw.value = ''
    }

    const optionStyle = (opt: any) => {
      const active = pendingCodes.value.has(opt.attrCode)
      return {
        background: active ? '#FFEDED' : '#f5f5f5',
        color: opt.locked ? '#bbb' : (active ? '#1a1a1a' : '#1a1a1a'),
        borderColor: active ? '#e8392a' : 'transparent',
        cursor: opt.locked ? 'not-allowed' : 'pointer'
      }
    }

    const pctColor = (v: number | undefined) => {
      if (v === undefined || v === null) return '#999'
      if (v > 0) return '#e8392a'
      if (v < 0) return '#00b578'
      return '#999'
    }

    const formatPct = (v: number | undefined) => {
      if (v === undefined || v === null) return ''
      const s = v > 0 ? v.toFixed(2) + '%' : v.toFixed(2) + '%'
      return s
    }

    // 全局点击关闭
    const onDocClick = (e: MouseEvent) => {
      if (showPopup.value) {
        const popup = document.querySelector('.fi-popup')
        if (popup && !popup.contains(e.target as Node)) onCancel()
      }
    }
    onMounted(() => document.addEventListener('click', onDocClick))
    onUnmounted(() => document.removeEventListener('click', onDocClick))

    watch(() => props.factor.attrCode, () => {
      asyncOptions.value = []
      pendingOptions.value = []
      rangeStockRows.value = []
    })

    return {
      showPopup, popupPos, loadingOptions, asyncOptions,
      pendingOptions, pendingCodes, unionMode, searchKw,
      showDescTip, optionListWithDesc, hasOptionDescs, descTipPos, onDescBtnEnter, onDescBtnLeave,
      isMulti, hasUnionSwitch, optionList, filteredOptions,
      rangeLeft, rangeRight, rangeError, rangeStockCount, rangeStockPct,
      chartCanvas, isRangeChanged, canConfirm, confirmDisabled, confirmBtnStyle,
      onRemoveTag, onClick, toggleOption, removePending, onCancel, onConfirm,
      optionStyle, pctColor, formatPct,
      onRangeInput, restoreDefault, onChartDrag,
      leftHandlePos, rightHandlePos, onHandleDrag,
      distMin, distMax, displaySubLabel, hasSubLabelArea
    }
  }
})
